<script setup lang="ts">
import { updateColorTokens } from '@/util/colorTokens'
import { Capacitor, registerPlugin } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import {
  AppVersionsInstance, // Used in template
  enableAnalytics,
  ForcedAppUpdate, // Used in template
  FullScreenImageViewPlugin,
  ScreenOrientationPlugin,
} from '@ghl/clientportal-core'
import {
  getSessionCookieData,
  isGokollab,
  isPwa,
  sendNotificationToken,
} from '@ghl/clientportal-core/init'
import { UIContentWrap } from '@gohighlevel/ghl-ui' // Used in template
import { useHead } from '@vueuse/head'
import { onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import moment from 'moment-timezone'

// Import moment locales
import 'moment/dist/locale/da'
import 'moment/dist/locale/de'
import 'moment/dist/locale/es'
import 'moment/dist/locale/fi'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/fr-ca'
import 'moment/dist/locale/it'
import 'moment/dist/locale/nl'
import 'moment/dist/locale/nb'
import 'moment/dist/locale/pt'
import 'moment/dist/locale/pt-br'
import 'moment/dist/locale/sv'

// Sync moment locale with vue-i18n locale
const { locale } = useI18n()
const momentLocaleMap = {
  'en': 'en',
  'de': 'de',
  'es': 'es',
  'da': 'da',
  'fi': 'fi',
  'fr-CA': 'fr-ca',
  'fr-FR': 'fr',
  'it': 'it',
  'nl': 'nl',
  'no': 'nb',
  'pt-BR': 'pt-br',
  'pt-PT': 'pt',
  'sv': 'sv'
}

watch(locale, (newLocale) => {
  const momentLocale = momentLocaleMap[newLocale] || 'en'
  moment.locale(momentLocale)
}, { immediate: true })

const WebViewBackgroundPlugin: any = registerPlugin('WebViewBackgroundPlugin')

function handlePushToken(event: Event) {
  const customEvent = event as CustomEvent
  const token = customEvent?.detail
  sendNotificationToken(token)
}

onMounted(async () => {
  // Initialize theme based on system preference before user data loads
  updateColorTokens()

  if (Capacitor.isNativePlatform()) {
    FullScreenImageViewPlugin.hideFullScreenView()
    if (Capacitor.isPluginAvailable('WebViewBackgroundPlugin')) {
      WebViewBackgroundPlugin.removeBackground()
    }
    await ScreenOrientationPlugin.lockPortraitOrientation()
  }
  if (isPwa()) {
    const cookie = await getSessionCookieData()
    if (!isGokollab()) {
      if (!cookie) {
        window.location.pathname = '/login'
        return false
      }
      // await validatePwaAuth()
    }
  }

  useHead({
    meta: [
      {
        name: 'viewport',
        content: isPwa()
          ? 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
          : 'width=device-width, initial-scale=1.0',
      },
    ],
  })
  if (isGokollab()) {
    enableAnalytics(null)
  }

  // window.addEventListener('push-notification', (event: Event) => {
  //   handleIOSPushRedirection(event)
  // })
  window.addEventListener('push-token', handlePushToken)
  if (Capacitor.getPlatform() === 'android') {
    SplashScreen.hide()
  }
})

onUnmounted(() => {
  window.removeEventListener('push-token', handlePushToken)
})
</script>
<template>
  <metainfo />
  <ForcedAppUpdate v-if="AppVersionsInstance.isForceUpdateRequired" />
  <UIContentWrap
    v-else
    class="outer-container default_font bg-communities-surface !text-communities-font-primary transition-colors duration-300"
    :full-screen="true"
  >
    <!-- <Loading /> -->
    <router-view />
  </UIContentWrap>
</template>
<style scoped>
.hl-wrapper-container {
  padding: 0px !important;
  background-color: var(--surface) !important;
  transition: background-color 0.3s ease;
}
@media (max-width: 930px) {
  .outer-container {
    padding: 0px !important;
  }
}
</style>
